import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useActualizarDensidad, useCrearDensidad, useDensidades } from '@/hooks/useDensidades';
import { cn } from '@/lib/utils';
import type { DensidadMaterial } from '@adelante/shared';
import { Check, Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';

/**
 * `/admin/densidades` — gestión del catálogo de densidades de materiales.
 *
 * Edita: Admin + Ingeniería (gateado por el backend con ROLES_CONFIG).
 *
 * Usos previstos:
 *   - Cálculo de líneas en el pedido de ensamblado BC (m³ → kg).
 *   - Validación cruzada de batches (qty teórica × densidad).
 *   - Rendimiento de concreto.
 */

interface FormNuevoState {
  clave: string;
  nombre: string;
  codigo_bc: string;
  densidad: string;
  unidad: string;
}

const FORM_NUEVO_VACIO: FormNuevoState = {
  clave: '',
  nombre: '',
  codigo_bc: '',
  densidad: '',
  unidad: 'kg/m³',
};

export function AdminDensidadesPantalla() {
  const consulta = useDensidades();
  const crear = useCrearDensidad();
  const actualizar = useActualizarDensidad();

  const densidades = consulta.data?.data?.densidades ?? [];
  const errorBackend = consulta.data?.error?.mensaje ?? null;

  // Form de nueva densidad.
  const [form, setForm] = useState<FormNuevoState>(FORM_NUEVO_VACIO);
  const handleCrear = () => {
    const claveTrim = form.clave.trim();
    const nombreTrim = form.nombre.trim();
    const densidadN = Number(form.densidad);
    if (!claveTrim || !nombreTrim || !Number.isFinite(densidadN) || densidadN <= 0) return;
    crear.mutate(
      {
        clave: claveTrim.toLowerCase(),
        nombre: nombreTrim,
        codigo_bc: form.codigo_bc.trim() || null,
        densidad: densidadN,
        unidad: form.unidad.trim() || 'kg/m³',
      },
      {
        onSuccess: (r) => {
          if (r.data) setForm(FORM_NUEVO_VACIO);
        },
      },
    );
  };

  // Edición inline.
  const [editClave, setEditClave] = useState<string | null>(null);
  const [editNombre, setEditNombre] = useState('');
  const [editCodBc, setEditCodBc] = useState('');
  const [editDensidad, setEditDensidad] = useState('');
  const [editUnidad, setEditUnidad] = useState('');

  const iniciarEdicion = (d: DensidadMaterial) => {
    setEditClave(d.clave);
    setEditNombre(d.nombre);
    setEditCodBc(d.codigo_bc ?? '');
    setEditDensidad(String(d.densidad));
    setEditUnidad(d.unidad);
  };
  const cancelar = () => setEditClave(null);
  const guardar = (d: DensidadMaterial) => {
    const dens = Number(editDensidad);
    if (!Number.isFinite(dens) || dens <= 0) return;
    actualizar.mutate(
      {
        clave: d.clave,
        nombre: editNombre.trim() || d.nombre,
        codigo_bc: editCodBc.trim() || null,
        densidad: dens,
        unidad: editUnidad.trim() || d.unidad,
      },
      {
        onSuccess: (r) => {
          if (r.data) setEditClave(null);
        },
      },
    );
  };
  const toggleActivo = (d: DensidadMaterial) => {
    actualizar.mutate({ clave: d.clave, activo: !d.activo });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Densidades de materiales</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Catálogo configurable de densidades (cemento, áridos, aditivos, etc.). Usado para
          conversiones kg ↔ m³ en pedidos de ensamblado, validación de batches y rendimientos.
        </p>
      </div>

      {/* Form nueva densidad */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Agregar densidad</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-[1fr_1.5fr_1fr_1fr_120px_auto] sm:items-end">
          <div className="grid gap-1.5">
            <Label htmlFor="d-clave">Clave</Label>
            <Input
              id="d-clave"
              value={form.clave}
              onChange={(e) => setForm({ ...form, clave: e.target.value.toLowerCase() })}
              placeholder="cemento_industrial"
              maxLength={60}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="d-nombre">Nombre</Label>
            <Input
              id="d-nombre"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Cemento Industrial"
              maxLength={100}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="d-cb">Código BC</Label>
            <Input
              id="d-cb"
              value={form.codigo_bc}
              onChange={(e) => setForm({ ...form, codigo_bc: e.target.value })}
              placeholder="M10-0047"
              maxLength={20}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="d-dens">Densidad</Label>
            <Input
              id="d-dens"
              type="number"
              step="any"
              value={form.densidad}
              onChange={(e) => setForm({ ...form, densidad: e.target.value })}
              placeholder="3150"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="d-uni">Unidad</Label>
            <Input
              id="d-uni"
              value={form.unidad}
              onChange={(e) => setForm({ ...form, unidad: e.target.value })}
              placeholder="kg/m³"
              maxLength={20}
            />
          </div>
          <Button
            onClick={handleCrear}
            disabled={!form.clave.trim() || !form.nombre.trim() || crear.isPending}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {crear.isPending ? 'Agregando…' : 'Agregar'}
          </Button>
        </CardContent>
      </Card>

      {errorBackend && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">{errorBackend}</CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">
            Catálogo{' '}
            <span className="font-normal text-muted-foreground">({densidades.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {consulta.isLoading &&
            ['s-a', 's-b', 's-c'].map((k) => (
              <Skeleton key={k} className="h-16 w-full rounded-md" />
            ))}

          {!consulta.isLoading && densidades.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No hay densidades en el catálogo.
            </div>
          )}

          {!consulta.isLoading &&
            densidades.map((d) => {
              const enEdicion = editClave === d.clave;
              return (
                <div
                  key={d.clave}
                  className={cn(
                    'flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between',
                    !d.activo && 'opacity-60',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-muted-foreground">{d.clave}</div>
                    {enEdicion ? (
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Input
                          value={editNombre}
                          onChange={(e) => setEditNombre(e.target.value)}
                          className="h-8 w-[200px] text-sm"
                          maxLength={100}
                        />
                        <Input
                          value={editCodBc}
                          onChange={(e) => setEditCodBc(e.target.value)}
                          placeholder="M10-…"
                          className="h-8 w-[100px] font-mono text-xs"
                          maxLength={20}
                        />
                      </div>
                    ) : (
                      <div className="text-sm">
                        {d.nombre}
                        {d.codigo_bc && (
                          <span className="ml-2 rounded-md border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-xs text-zinc-700">
                            {d.codigo_bc}
                          </span>
                        )}
                        {d.notas && (
                          <div className="mt-0.5 text-xs italic text-muted-foreground">
                            {d.notas}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {enEdicion ? (
                      <>
                        <Input
                          type="number"
                          step="any"
                          value={editDensidad}
                          onChange={(e) => setEditDensidad(e.target.value)}
                          className="h-8 w-[100px] text-right font-mono"
                        />
                        <Input
                          value={editUnidad}
                          onChange={(e) => setEditUnidad(e.target.value)}
                          className="h-8 w-[80px] font-mono"
                          maxLength={20}
                        />
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          disabled={actualizar.isPending}
                          onClick={() => guardar(d)}
                          title="Guardar"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={cancelar}
                          title="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="min-w-[110px] text-right font-mono text-sm font-semibold">
                          {d.densidad}
                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                            {d.unidad}
                          </span>
                        </span>
                        <span
                          className={cn(
                            'inline-flex rounded-full border px-2 py-0.5 text-xs',
                            d.activo
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                              : 'border-zinc-300 bg-zinc-100 text-zinc-500',
                          )}
                        >
                          {d.activo ? 'Activo' : 'Inactivo'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 gap-1.5"
                          onClick={() => iniciarEdicion(d)}
                          disabled={actualizar.isPending}
                        >
                          <Pencil className="h-3.5 w-3.5" /> Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() => toggleActivo(d)}
                          disabled={actualizar.isPending}
                        >
                          {d.activo ? 'Desactivar' : 'Activar'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
        </CardContent>
      </Card>
    </div>
  );
}
